import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Leaf, Bug, Shield, BarChart2, CheckCircle, Smartphone, Wifi, Lightbulb, MapPin } from 'lucide-react';

const WALKTHROUGH_KEY = 'gg_walkthrough_completed';

const steps = [
  {
    id: 'welcome',
    title: 'Welcome to GrowGuardian IPM',
    icon: <Leaf className="w-8 h-8 text-green-600" />,
    color: 'green',
    target: null,
    content: (
      <div>
        <p className="text-gray-600 text-sm leading-relaxed mb-3">
          <strong>Integrated Pest Management (IPM)</strong> is a science-based approach to controlling pests — insects, weeds, and diseases — using the least harmful methods first.
        </p>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          This dashboard is your command center for monitoring fields, tracking treatments, and managing pest control — whether you run a small garden or thousands of acres.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">🌱 Small Scale</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">🏭 Large Scale</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">✓ All Skill Levels</span>
        </div>
      </div>
    )
  },
  {
    id: 'ipm-cycle',
    title: 'The IPM Monitoring Cycle',
    icon: <Bug className="w-8 h-8 text-orange-500" />,
    color: 'orange',
    target: null,
    content: (
      <div>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          IPM follows a repeating cycle. GrowGuardian guides you through each stage automatically:
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
            <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Scout & Monitor</p>
              <p className="text-xs text-gray-600">Regularly inspect fields and log pest sightings. Your field team submits observations via the mobile app.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
            <div className="w-7 h-7 rounded-full bg-yellow-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Set Action Thresholds</p>
              <p className="text-xs text-gray-600">Decide when pest levels are high enough to require treatment. GrowGuardian alerts you when thresholds are crossed.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Apply Treatment</p>
              <p className="text-xs text-gray-600">Choose the least harmful effective method — biological, cultural, or chemical. Log every application for compliance.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
            <div className="w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">4</div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Evaluate & Improve</p>
              <p className="text-xs text-gray-600">Review treatment effectiveness in Treatment Analytics. AI insights help you improve your strategy over time.</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'kpi-metrics',
    title: 'Key Metrics — At a Glance',
    icon: <BarChart2 className="w-8 h-8 text-blue-600" />,
    color: 'blue',
    target: 'kpi-section',
    content: (
      <div>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          The <strong>Today's Overview</strong> cards at the top show your most important numbers right now:
        </p>
        <div className="space-y-2.5">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Total Tasks</p>
              <p className="text-xs text-gray-600">All IPM tasks assigned today — scouting, treatments, and follow-ups.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Completed / In Progress</p>
              <p className="text-xs text-gray-600">Track how much work is done vs. still active. Aim for a high completion rate.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Smartphone className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Field Devices</p>
              <p className="text-xs text-gray-600">How many mobile devices are active in the field right now, submitting live data.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Wifi className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Sync Health</p>
              <p className="text-xs text-gray-600">The percentage of data syncs that succeeded. Keep this above 90% for reliable reporting.</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'field-status',title: 'Field Status & Pest Pressure',
    icon: <MapPin className="w-8 h-8 text-red-500" />,
    color: 'red',target: 'field-status-section',
    content: (
      <div>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          The <strong>Field Status</strong> grid shows each of your fields and their current pest pressure level:
        </p>
        <div className="space-y-2.5">
          <div className="flex items-center gap-3 p-2.5 bg-green-50 rounded-lg">
            <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Low / Clear</p>
              <p className="text-xs text-gray-600">Pest levels are below action thresholds. No treatment needed yet.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2.5 bg-yellow-50 rounded-lg">
            <div className="w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0"></div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Moderate / Watch</p>
              <p className="text-xs text-gray-600">Pest activity detected. Increase scouting frequency and prepare a response plan.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2.5 bg-red-50 rounded-lg">
            <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></div>
            <div>
              <p className="text-sm font-semibold text-gray-800">High / Action Required</p>
              <p className="text-xs text-gray-600">Threshold exceeded. Immediate treatment is recommended to prevent crop damage.</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 italic">Tip: Click any field card to see detailed scouting history and treatment logs.</p>
      </div>
    )
  },
  {
    id: 'tasks-timeline',title: 'Tasks & Treatment Timeline',
    icon: <Shield className="w-8 h-8 text-purple-600" />,
    color: 'purple',target: 'tasks-section',
    content: (
      <div>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          Two key sections help you stay on top of your IPM program:
        </p>
        <div className="space-y-3">
          <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
            <p className="text-sm font-semibold text-gray-800 mb-1">📅 Treatment Timeline</p>
            <p className="text-xs text-gray-600">A visual calendar of all scheduled and completed treatments. Use this to avoid re-treating too soon (re-entry intervals) and to spot gaps in your program.</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg border border-green-100">
            <p className="text-sm font-semibold text-gray-800 mb-1">✅ Active IPM Tasks</p>
            <p className="text-xs text-gray-600">A live list of what your team is working on right now — scouting assignments, treatment applications, and follow-up checks. Tasks are assigned from the Task Management screen.</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 italic">Tip: Use the Quick Actions sidebar to create a new task or schedule a treatment in seconds.</p>
      </div>
    )
  },
  {
    id: 'ai-tools',title: 'AI-Powered Tools',
    icon: <Lightbulb className="w-8 h-8 text-yellow-500" />,
    color: 'yellow',
    target: null,
    content: (
      <div>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          GrowGuardian includes AI tools that learn from your data and help you make better decisions:
        </p>
        <div className="space-y-2.5">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xs">🤖</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">AI Treatment Suggestions</p>
              <p className="text-xs text-gray-600">The sidebar shows AI-recommended treatments based on current weather, pest pressure, and your history.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xs">💬</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">AI Chat Assistant</p>
              <p className="text-xs text-gray-600">Ask any IPM question in plain English. Upload photos of pests or plant damage for instant AI identification.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xs">📊</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Treatment Analytics</p>
              <p className="text-xs text-gray-600">See which treatments are working best across your fields. Powered by AI pattern recognition.</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'done',
    title: "You\'re Ready to Go!",
    icon: <CheckCircle className="w-8 h-8 text-green-600" />,
    color: 'green',
    target: null,
    content: (
      <div>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          You now know the essentials of the IPM Operations Dashboard. Here's a quick recap of where to start:
        </p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-green-600 font-bold">1.</span> Check your <strong>Field Status</strong> cards for any high-pressure areas
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-green-600 font-bold">2.</span> Review <strong>Active Tasks</strong> to see what your team is doing
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-green-600 font-bold">3.</span> Check <strong>AI Treatment Suggestions</strong> in the sidebar
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-green-600 font-bold">4.</span> Visit <strong>IPM Learning Center</strong> to deepen your knowledge
          </div>
        </div>
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-xs text-green-800 font-medium">💡 You can replay this walkthrough anytime from the "?" button in the dashboard header.</p>
        </div>
      </div>
    )
  }
];

const colorMap = {
  green: { bg: 'bg-green-50', border: 'border-green-200', btn: 'bg-green-600 hover:bg-green-700', dot: 'bg-green-600', progress: 'bg-green-600' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', btn: 'bg-orange-500 hover:bg-orange-600', dot: 'bg-orange-500', progress: 'bg-orange-500' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', btn: 'bg-blue-600 hover:bg-blue-700', dot: 'bg-blue-600', progress: 'bg-blue-600' },
  red: { bg: 'bg-red-50', border: 'border-red-200', btn: 'bg-red-500 hover:bg-red-600', dot: 'bg-red-500', progress: 'bg-red-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', btn: 'bg-purple-600 hover:bg-purple-700', dot: 'bg-purple-600', progress: 'bg-purple-600' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', btn: 'bg-yellow-500 hover:bg-yellow-600', dot: 'bg-yellow-500', progress: 'bg-yellow-500' },
};

const GuidedWalkthrough = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const modalRef = useRef(null);

  const step = steps?.[currentStep];
  const colors = colorMap?.[step?.color] || colorMap?.green;
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps?.length - 1;
  const progress = ((currentStep + 1) / steps?.length) * 100;

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      localStorage.setItem(WALKTHROUGH_KEY, 'true');
      onClose?.();
    }, 200);
  };

  const handleNext = () => {
    if (isLast) {
      handleClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) setCurrentStep(prev => prev - 1);
  };

  const handleSkip = () => {
    handleClose();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e?.key === 'ArrowRight' || e?.key === 'Enter') handleNext();
      if (e?.key === 'ArrowLeft') handlePrev();
      if (e?.key === 'Escape') handleSkip();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentStep]);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${isExiting ? 'opacity-0' : 'opacity-100'}`}
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e?.target === e?.currentTarget) handleSkip(); }}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-auto overflow-hidden transition-transform duration-200 ${isExiting ? 'scale-95' : 'scale-100'}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Guided walkthrough step ${currentStep + 1} of ${steps?.length}: ${step?.title}`}
      >
        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100 w-full">
          <div
            className={`h-full transition-all duration-500 ${colors?.progress}`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className={`px-6 pt-5 pb-4 ${colors?.bg} border-b ${colors?.border}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">{step?.icon}</div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">
                  Step {currentStep + 1} of {steps?.length}
                </p>
                <h2 className="text-lg font-bold text-gray-900 leading-tight">{step?.title}</h2>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-white/70 transition-colors"
              aria-label="Close walkthrough"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 max-h-80 overflow-y-auto">
          {step?.content}
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5 pb-2">
          {steps?.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentStep(i)}
              className={`rounded-full transition-all duration-200 ${i === currentStep ? `w-5 h-2 ${colors?.dot}` : 'w-2 h-2 bg-gray-200 hover:bg-gray-300'}`}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-2 flex items-center justify-between gap-3">
          <button
            onClick={handleSkip}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors px-2 py-1"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className={`flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors ${colors?.btn}`}
            >
              {isLast ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Get Started
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { WALKTHROUGH_KEY };
export default GuidedWalkthrough;
